import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Stack,
  IconButton,
  Icon,
} from '@worthy-npm/worthy-common-ui-components';
import { debounce } from 'lodash';
import {
  Location, Street, State, LocationPoper,
} from './addressInput.styles';
import { Address } from '../../../slices/schedulingSlice';

import {
  AddressSuggestion,
  getPlaceDetails,
  getPlacePredictions,
  initializeGoogleMapsServices,
} from '../../../lib/googleMapsHelper';

export type AddressForAutocomplete = Pick<Address, 'street' | 'city' | 'state' | 'zipcode'>;

const LocationIcon = Icon.createSvgIcon(
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="19" viewBox="0 0 12 19" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.92907 2.25C7.20116 2.25 8.17902 2.66911 8.83882 3.34438C9.50033 4.02139 9.92907 5.04534 9.92907 6.41406C9.92907 7.73946 9.17954 9.00656 8.15103 10.5534C8.08041 10.6597 8.00828 10.7674 7.93513 10.8767L7.93497 10.8769C7.32236 11.7921 6.63853 12.8136 6.1766 13.9258C5.65226 12.84 4.92299 11.8539 4.26677 10.9666L4.26673 10.9666C4.18532 10.8565 4.10503 10.7479 4.0264 10.6409C2.87565 9.07435 2.03375 7.78716 2.00165 6.39223C1.97026 5.02849 2.38318 4.01196 3.03212 3.34072C3.6808 2.66974 4.6557 2.25 5.92907 2.25ZM11.9291 6.41406C11.9291 4.61872 11.3578 3.06064 10.2693 1.94664C9.17912 0.830888 7.65698 0.25 5.92907 0.25C4.20244 0.25 2.67734 0.830255 1.59422 1.95059C0.511377 3.07066 -0.039281 4.63674 0.00217994 6.43824C0.0509698 8.55825 1.32201 10.3376 2.41456 11.8249L2.57432 12.0422C3.68082 13.5461 4.60462 14.8017 4.84493 16.1836L5.29131 18.7505L7.26631 18.7218L7.67035 15.9171C7.86033 14.5984 8.68409 13.3614 9.67299 11.8764L9.67299 11.8764L9.8165 11.6608C10.7929 10.1922 11.9291 8.43525 11.9291 6.41406ZM6 8C7.10457 8 8 7.10457 8 6C8 4.89543 7.10457 4 6 4C4.89543 4 4 4.89543 4 6C4 7.10457 4.89543 8 6 8Z"
      fill="#2C4EB5"
    />
  </svg>,
  'LocationIcon',
);

const addressToFormattedAddress = (obj: Pick<Address, 'city' | 'street' | 'state' | 'zipcode'>) => {
  const {
    street, city, state, zipcode,
  } = obj;
  return `${street}, ${city}, ${state}, ${zipcode}`;
};

function AutoCompleteAddress({
  address,
  setAddress,
}: {
  address: AddressForAutocomplete;
  setAddress: (address: AddressForAutocomplete) => void;
}) {
  const [autocompleteService, setAutocompleteService] = useState<google.maps.places.AutocompleteService | null>(null);
  const [placesService, setPlacesService] = useState<google.maps.places.PlacesService | null>(null);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [inputValue, setInputValue] = useState(address.street || '');
  const [selectedSuggestion, setSelectedSuggestion] = useState<AddressSuggestion | null>(null);
  const [isEditing, setIsEditing] = useState(!address.street);

  useEffect(() => {
    const { autocompleteService: newAutocompleteService, placesService: newPlacesService } = initializeGoogleMapsServices();
    setAutocompleteService(newAutocompleteService);
    setPlacesService(newPlacesService);
  }, []);

  const fetch = useMemo(
    () => debounce((input: string, callback: (results?: readonly AddressSuggestion[]) => void) => {
      if (autocompleteService) {
        return getPlacePredictions(autocompleteService, input).then((results) => {
          callback(results);
        });
      }
      return undefined;
    }, 400),
    [autocompleteService],
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService) {
      return undefined;
    }

    if (inputValue === '') {
      setSuggestions(selectedSuggestion ? [selectedSuggestion] : []);
      return undefined;
    }

    if (selectedSuggestion?.description === inputValue) {
      return undefined;
    }
    fetch(inputValue, (results?: readonly AddressSuggestion[]) => {
      if (active) {
        let newOptions: readonly AddressSuggestion[] = [];

        if (selectedSuggestion) {
          newOptions = [selectedSuggestion];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setSuggestions(newOptions as AddressSuggestion[]);
      }
    });

    return () => {
      active = false;
    };
  }, [selectedSuggestion, inputValue, fetch, autocompleteService]);

  const updateAddress = (suggestion: AddressSuggestion) => {
    if (placesService) {
      getPlaceDetails(placesService, suggestion.place_id)
        .then(({
          addressStreet, addressCity, addressState, addressZipcode,
        }) => {
          setAddress({
            street: addressStreet,
            city: addressCity,
            state: addressState,
            zipcode: addressZipcode,
          });
        })
        .catch((error) => {
          console.error('Error fetching place details:', error);
        });
    }
  };

  const onEditEnable = () => {
    setInputValue('');
    setIsEditing(true);
  };
  return (
    <Stack direction="row" width="100%">
      <Autocomplete
        getOptionLabel={(option: AddressSuggestion) => (typeof option === 'string' ? option : option.description)}
        options={suggestions}
        autoComplete
        includeInputInList
        fullWidth
        disabled={!isEditing}
        filterOptions={(x) => x}
        value={selectedSuggestion}
        PaperComponent={LocationPoper}
        open={!!inputValue}
        slotProps={{
          popper: {
            open: !!inputValue && !selectedSuggestion && isEditing,
          },
        }}
        ListboxProps={{
          sx: (theme) => ({
            padding: 0,
            backgroundColor: 'inherit',
            '& li.MuiAutocomplete-option': {
              paddingTop: theme.spacing(1),
              paddingBottom: theme.spacing(1.5),
              paddingLeft: theme.spacing(1.5),
              paddingRight: theme.spacing(0),
              borderBottom: `1px solid ${theme.palette.divider}`,
            },
            '& li.MuiAutocomplete-option.Mui-focused': {
              backgroundColor: theme.palette.background.default,
            },
          }),
        }}
        noOptionsText={<State>No locations</State>}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={(e, newValue: AddressSuggestion | null) => {
          if (newValue) {
            updateAddress(newValue);
          }
          setSuggestions(newValue ? [newValue, ...suggestions] : suggestions);
          setSelectedSuggestion(newValue);
        }}
        renderInput={(params) => (
          <TextField
            variant="standard"
            {...params}
            label={isEditing ? 'Street Address' : addressToFormattedAddress(address)}
            value={inputValue}
            fullWidth
            InputLabelProps={{
              sx: {
                paddingLeft: 2,
                color: 'secondary.main',
                '&.Mui-focused': {
                  color: 'secondary.main',
                },
                '&.Mui-disabled': {
                  color: 'text.primary',
                  paddingLeft: 0,
                },
              },
            }}
          />
        )}
        renderOption={(props, option: AddressSuggestion) => {
          const { ...optionProps } = props;

          return (
            <Location {...optionProps} key={option.place_id}>
              <LocationIcon />
              <Stack gap={0.5}>
                <Street>{option.structured_formatting.main_text}</Street>
                <State fontSize={12} color="secondary.main">
                  {option.structured_formatting.secondary_text}
                </State>
              </Stack>
            </Location>
          );
        }}
      />
      {!isEditing && (
        <IconButton
          onClick={onEditEnable}
          disableRipple
          sx={{
            borderRadius: '0px',
            p: 0.5,
            marginLeft: '1px',
            borderBottom: '1px dotted rgba(0, 0, 0, 0.42)',
            alignItems: 'end',
          }}
        >
          <Icon.Edit fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}

export default AutoCompleteAddress;
